import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import type { UserProfile } from '../types';

const COUNTRIES = [
  { value: 'AR', label: 'Argentina' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'BR', label: 'Brasil' },
  { value: 'CA', label: 'Canadá' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CU', label: 'Cuba' },
  { value: 'DE', label: 'Alemania' },
  { value: 'DO', label: 'República Dominicana' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'ES', label: 'España' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'FR', label: 'Francia' },
  { value: 'GB', label: 'Reino Unido' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'HN', label: 'Honduras' },
  { value: 'IT', label: 'Italia' },
  { value: 'JP', label: 'Japón' },
  { value: 'MX', label: 'México' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'PA', label: 'Panamá' },
  { value: 'PE', label: 'Perú' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'US', label: 'Estados Unidos' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'VE', label: 'Venezuela' },
];

// ── Star display ─────────────────────────────────────────────────────────────
function StarDisplay({ avg, count }: { avg?: number | null; count?: number }) {
  // Always show 5 stars — gray if no ratings, colored if has ratings
  const filled = avg ? Math.round(avg) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <span key={s} className={`text-xl ${s <= filled ? '' : 'grayscale opacity-30'}`}>
            ⭐
          </span>
        ))}
      </div>
      {avg && count ? (
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-gray-700">{avg.toFixed(1)}</span>
          <span className="text-xs text-gray-400 font-semibold">
            ({count} intercambio{count !== 1 ? 's' : ''})
          </span>
        </div>
      ) : (
        <span className="text-xs text-gray-400 font-semibold">Sin calificaciones aún</span>
      )}
    </div>
  );
}

// ── Avatar with photo upload ──────────────────────────────────────────────────
function AvatarUpload({
  username,
  photoUrl,
  onUpload,
  onRemove,
  uploading,
  removing,
}: {
  username: string;
  photoUrl?: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
  uploading: boolean;
  removing: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <div
        className="w-20 h-20 rounded-2xl bg-white border-4 border-white shadow-md flex items-center justify-center overflow-hidden cursor-pointer group"
        onClick={() => inputRef.current?.click()}
      >
        {photoUrl ? (
          <img src={photoUrl} alt={username} className="w-full h-full object-cover" />
        ) : (
          <span className="text-2xl font-bold text-brand-600" style={{ fontFamily: '"Fredoka One", cursive' }}>
            {initials}
          </span>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {uploading ? (
            <Spinner size={20} />
          ) : (
            <span className="text-white text-xs font-bold">📷</span>
          )}
        </div>
      </div>
      {/* Remove button — only shown when a photo exists */}
      {photoUrl && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          disabled={removing}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-colors disabled:opacity-50"
          title="Quitar foto"
        >
          {removing ? <Spinner size={10} /> : <span className="text-[10px] font-bold leading-none">✕</span>}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  const [form, setForm] = useState({
    isPublic: true,
    pais:     '',
    ciudad:   '',
    whatsapp: '',
    nota:     '',
  });
  const [paisError, setPaisError] = useState('');

  const { data: profile, isLoading, error } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get('/profile');
      return data.data;
    },
    retry: false,
  });

  function startEdit() {
    if (profile) {
      setForm({
        isPublic: profile.isPublic,
        pais:     profile.pais     ?? '',
        ciudad:   profile.ciudad   ?? '',
        whatsapp: profile.whatsapp ?? '',
        nota:     profile.nota     ?? '',
      });
    }
    setPaisError('');
    setEditing(true);
  }

  const update = useMutation({
    mutationFn: async (payload: typeof form) => {
      const { data } = await api.put('/profile', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setEditing(false);
    },
  });

  // Auto-guardar pais pendiente del registro si el perfil no lo tiene
  useEffect(() => {
    const pendingPais = localStorage.getItem('pending_pais');
    if (profile && !profile.pais && pendingPais) {
      localStorage.removeItem('pending_pais');
      api.put('/profile', { pais: pendingPais }).then(() => {
        qc.invalidateQueries({ queryKey: ['profile'] });
      }).catch(() => {});
    }
  }, [profile, qc]);

  async function handlePhotoRemove() {
    setRemovingPhoto(true);
    try {
      await api.delete('/profile/photo');
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch {
      // silently ignore — user can retry
    } finally {
      setRemovingPhoto(false);
    }
  }

  async function handlePhotoUpload(file: File) {
    setUploadingPhoto(true);
    try {
      // Wrap FileReader in a Promise so errors propagate correctly
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = (e) => resolve((e.target?.result as string).split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      await api.post('/profile/photo', { imageBase64: base64, contentType: file.type });
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch {
      // silently ignore — user can retry
    } finally {
      setUploadingPhoto(false);
    }
  }

  if (isLoading) {
    return <div className="flex justify-center items-center py-20"><Spinner size={40} /></div>;
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col gap-5 max-w-lg">
        <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
          <h1 className="text-2xl font-bold text-night mb-2" style={{ fontFamily: '"Fredoka One", cursive' }}>
            Mi Perfil
          </h1>
          <p className="text-sm text-red-500 font-semibold">
            Error al cargar el perfil. Intenta recargar la página.
          </p>
        </div>
      </div>
    );
  }

  const memberSince = profile.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('es', { dateStyle: 'long' })
    : '—';

  return (
    <div className="flex flex-col gap-5 max-w-lg">

      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-card border border-amber-100 overflow-hidden">
        {/* Banner */}
        <div className="h-20 bg-gradient-to-br from-brand-600 to-brand-700" />

        <div className="px-6 pb-5">
          {/* Avatar + edit button */}
          <div className="-mt-10 mb-3 flex items-end justify-between">
            <AvatarUpload
              username={authUser?.username ?? profile.username}
              photoUrl={(profile as UserProfile & { photoUrl?: string }).photoUrl}
              onUpload={handlePhotoUpload}
              onRemove={handlePhotoRemove}
              uploading={uploadingPhoto}
              removing={removingPhoto}
            />
            {!editing && (
              <Button variant="secondary" size="sm" onClick={startEdit}>Editar</Button>
            )}
          </div>

          {/* Name + email */}
          <h2 className="text-xl font-bold text-gray-900">{authUser?.username ?? profile.username}</h2>
          <p className="text-sm text-gray-400 font-semibold">{profile.email}</p>
          {profile.pais && (
            <p className="text-sm text-gray-500 font-semibold mt-0.5">
              🌎 {COUNTRIES.find((c) => c.value === profile.pais)?.label ?? profile.pais}
            </p>
          )}
          {profile.ciudad && (
            <p className="text-sm text-gray-500 font-semibold mt-0.5">📍 {profile.ciudad}</p>
          )}

          {/* Rating */}
          <div className="mt-3 pt-3 border-t border-gray-50">
            <StarDisplay avg={profile.ratingAvg} count={profile.ratingCount} />
          </div>
        </div>
      </div>

      {/* Banner: país no configurado */}
      {!editing && !profile.pais && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-xl flex-shrink-0">🌎</span>
          <p className="text-xs text-amber-700 font-semibold leading-relaxed">
            Configura tu país para aparecer en búsquedas de tu región.{' '}
            <button
              className="underline font-bold"
              onClick={startEdit}
            >
              Editar perfil
            </button>
          </p>
        </div>
      )}

      {/* Edit form */}
      {editing ? (
        <div className="bg-white rounded-2xl p-5 shadow-card border border-brand-100">
          <h3 className="font-bold text-gray-700 mb-4">Editar perfil</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.pais) {
                setPaisError('Selecciona tu país para aparecer en búsquedas locales');
                return;
              }
              setPaisError('');
              update.mutate(form);
            }}
            className="flex flex-col gap-4"
          >
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                🌎 País
              </label>
              <select
                value={form.pais}
                onChange={(e) => { setForm({ ...form, pais: e.target.value }); if (e.target.value) setPaisError(''); }}
                className="input-field"
              >
                <option value="">-- Selecciona tu país --</option>
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {paisError && (
                <p className="text-xs text-red-500 font-semibold mt-1">{paisError}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                📍 Ciudad
              </label>
              <input
                value={form.ciudad}
                onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                placeholder="ej: Ciudad de México, Monterrey..."
                maxLength={100}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                📱 WhatsApp / Teléfono
              </label>
              <input
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                placeholder="ej: +52 55 1234 5678"
                maxLength={30}
                className="input-field"
              />
              <p className="text-xs text-gray-400 mt-1">
                Solo se comparte con usuarios con quienes tengas un intercambio aceptado
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                📝 Nota para intercambios
              </label>
              <textarea
                value={form.nota}
                onChange={(e) => setForm({ ...form, nota: e.target.value })}
                placeholder="ej: Solo intercambios en CDMX, puedo enviar por correo..."
                maxLength={300}
                rows={3}
                className="input-field resize-none text-sm"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="relative mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={form.isPublic}
                  onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-10 h-6 bg-gray-200 peer-checked:bg-brand-500 rounded-full transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
              </div>
              <div>
                <div className="font-bold text-sm text-gray-700">Perfil público</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Otros usuarios pueden ver tu inventario y figuritas duplicadas
                </div>
              </div>
            </label>

            {update.isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                Error al guardar. Intenta de nuevo.
              </div>
            )}

            <div className="flex gap-2">
              <Button type="submit" loading={update.isPending}>Guardar cambios</Button>
              <Button variant="secondary" onClick={() => setEditing(false)}>Cancelar</Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
          <h3 className="font-bold text-gray-700 mb-4 text-sm uppercase tracking-wide">Mi información</h3>
          <div className="flex flex-col gap-0">
            <InfoRow label="País"     value={COUNTRIES.find((c) => c.value === profile.pais)?.label ?? (profile.pais || '—')} />
            <InfoRow label="Ciudad"   value={profile.ciudad   || '—'} />
            <InfoRow label="WhatsApp" value={profile.whatsapp || '—'} />
            {profile.nota && (
              <div className="py-2 border-b border-gray-50">
                <span className="text-sm text-gray-400 font-semibold block mb-1">Nota</span>
                <span className="text-sm font-semibold text-gray-700">{profile.nota}</span>
              </div>
            )}
            <InfoRow label="Perfil público" value={profile.isPublic ? '✓ Visible para todos' : '✗ Privado'} />
            <InfoRow label="Miembro desde"  value={memberSince} />
          </div>
        </div>
      )}

      {/* Tip */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <span className="text-xl flex-shrink-0">💡</span>
        <p className="text-xs text-blue-700 font-semibold leading-relaxed">
          Tu ciudad y WhatsApp solo se comparten cuando alguien acepta un intercambio contigo.
        </p>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-400 font-semibold">{label}</span>
      <span className="text-sm font-bold text-gray-700">{value}</span>
    </div>
  );
}
